import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Heart, Menu, UserRound } from "lucide-react";
import { useCustomer } from "../lib/customer";
import { ThemePicker, ModeToggle, MobileThemeControls } from "./ThemeControls";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Logo } from "./Logo";
import { useI18n } from "../lib/i18n";
import { useFavorites } from "../lib/store";

const PROPERTY_LINKS = [
  ["/properties", "nav.allProperties"], ["/properties/for-sale", "nav.forSale"], ["/properties/for-rent", "nav.forRent"],
  ["/residential", "nav.residential"], ["/commercial", "nav.commercial"], ["/land-investment", "nav.land"],
];
const ABOUT_LINKS = [["/about", "nav.aboutUs"], ["/vision-mission", "nav.vision"], ["/services", "nav.services"]];
const MAIN_LINKS = [["/developments", "nav.developments"], ["/investment", "nav.investment"], ["/news", "nav.news"], ["/contact", "nav.contact"]];

function NavDrop({ label, links, id }) {
  const { t, isRtl } = useI18n();
  return (
    <DropdownMenu dir={isRtl ? "rtl" : "ltr"}>
      <DropdownMenuTrigger data-testid={`nav-${id}-menu`} className="inline-flex items-center gap-1 text-sm text-fg/80 transition-colors hover:text-cta focus:outline-none">
        {label} <ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[220px] border-cta/20 bg-surface/95 backdrop-blur-xl">
        {links.map(([to, key]) => (
          <DropdownMenuItem key={to} asChild className="cursor-pointer py-2.5 focus:bg-brand focus:text-cta">
            <Link to={to} data-testid={`nav-link-${to.replace(/\//g, "-").slice(1)}`}>{t(key)}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const LangToggle = ({ className = "" }) => {
  const { t, lang, setLang } = useI18n();
  return (
    <button type="button" data-testid="lang-toggle" onClick={() => setLang(lang === "en" ? "ar" : "en")} className={`rounded-full border border-cta/40 px-3 py-1.5 text-xs font-semibold text-cta transition-colors hover:bg-cta hover:text-canvas ${className}`} aria-label={lang === "en" ? "التبديل إلى العربية" : "Switch to English"}>
      {t("nav.switchLang")}
    </button>
  );
};

const AccountLink = () => {
  const { customer } = useCustomer() || {};
  return (
    <Link to="/account" data-testid="nav-account-link" aria-label="Account" className="relative p-2 text-fg/80 transition-colors hover:text-cta">
      {customer ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cta text-xs font-bold uppercase text-canvas">{customer.name?.[0]}</span> : <UserRound className="h-5 w-5" />}
    </Link>
  );
};

const FavLink = () => {
  const { favs } = useFavorites();
  const { t } = useI18n();
  return (
    <Link to="/favorites" data-testid="nav-favorites-link" className="relative p-2 text-fg/80 transition-colors hover:text-cta" aria-label={t("nav.favorites")}>
      <Heart className="h-5 w-5" />
      {favs.length > 0 && <span data-testid="nav-favorites-count" className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-cta px-1 text-[10px] font-bold text-canvas">{favs.length}</span>}
    </Link>
  );
};

function MobileMenu() {
  const { t, isRtl } = useI18n();
  const [open, setOpen] = useState(false);
  const all = [["/", "nav.home"], ...PROPERTY_LINKS, ...MAIN_LINKS, ...ABOUT_LINKS, ["/favorites", "nav.favorites"], ["/schedule-viewing", "nav.schedule"]];
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger data-testid="mobile-menu-button" className="p-2 text-fg lg:hidden" aria-label={t("nav.menu")}><Menu className="h-6 w-6" /></SheetTrigger>
      <SheetContent side={isRtl ? "left" : "right"} className="w-[300px] overflow-y-auto border-cta/20 bg-canvas text-fg" dir={isRtl ? "rtl" : "ltr"}>
        <SheetTitle className="eyebrow mb-6">{t("nav.menu")}</SheetTitle>
        <nav className="flex flex-col gap-1">
          {all.map(([to, key]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${to === "/" ? "home" : to.replace(/\//g, "-").slice(1)}`} className="border-b border-fg/5 py-3 text-base text-fg/85 hover:text-cta">{t(key)}</Link>
          ))}
        </nav>
        <LangToggle className="mt-6" />
        <MobileThemeControls />
      </SheetContent>
    </Sheet>
  );
}

export function Navbar() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const solid = scrolled || pathname !== "/";
  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${solid ? "border-b border-cta/15 bg-canvas/90 backdrop-blur-xl" : "on-media border-b border-transparent bg-transparent"}`}>
      <div className="container-lux flex h-20 items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" aria-label="Landmark Future — Home">
          <Logo />
          <span className="hidden leading-tight sm:block">
            <span className="block font-display text-lg text-fg">Landmark Future</span>
            <span className="block font-[Tajawal] text-[11px] text-cta" lang="ar">لاندمارك فيوتشر للتطوير العقاري</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main">
          <NavDrop id="properties" label={t("nav.properties")} links={PROPERTY_LINKS} />
          {MAIN_LINKS.map(([to, key]) => (
            <NavLink key={to} to={to} data-testid={`nav-link-${to.slice(1)}`} className={({ isActive }) => `text-sm transition-colors hover:text-cta ${isActive ? "text-cta" : "text-fg/80"}`}>{t(key)}</NavLink>
          ))}
          <NavDrop id="about" label={t("nav.about")} links={ABOUT_LINKS} />
        </nav>
        <div className="flex items-center gap-2">
          <FavLink />
          <AccountLink />
          <ThemePicker />
          <ModeToggle className="hidden sm:inline-flex" />
          <LangToggle className="hidden sm:inline-flex" />
          <Link to="/schedule-viewing" data-testid="nav-schedule-cta" className="btn-cta hidden !px-5 !py-2.5 xl:inline-flex">{t("nav.schedule")}</Link>
          <MobileMenu />
        </div>
      </div>
    </header>
  );
}
